import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useKioskStore } from '@/store/kiosk-store';
import { selectEnabledModules } from '@/store/selectors';
import { useHandoff } from '@/hooks/use-handoff';
import ServiceCard from '@/components/ServiceCard';
import HandoffLoader from '@/components/HandoffLoader';
import { resolveModuleIconify } from '@museum-kiosk/shared-types';

/**
 * Module selector screen — adapted from Lovable KioskHome.
 * Modules come from Zustand store (no hardcoded services).
 * Navigation uses useHandoff, not react-router-dom.
 */
const ModuleSelector: React.FC = () => {
  const modules = useKioskStore(useShallow(selectEnabledModules));
  const isOnline = useKioskStore((s) => s.isOnline);
  const config = useKioskStore((s) => s.config);
  const { createHandoff, isLoading, loadingUrl } = useHandoff();

  // Institution-wide display name (e.g. "City Art Museum"), sourced from
  // branding — deliberately NOT config.location, which is the per-kiosk
  // physical descriptor (e.g. "North Wing Entrance").
  const institutionName = config?.branding.location_name ?? 'Museum Kiosk';
  const loadingModule = modules.find((m) => m.url === loadingUrl);

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-center px-8 py-6 bg-content1 border-b border-divider">
        <hgroup className="text-center animate-kiosk-fade-in">
          <p className="text-sm font-medium text-default-600 tracking-wide">
            {institutionName}
          </p>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            How Can We Help You?
          </h1>
          <p className="text-base text-default-500 mt-1.5">
            Select a service to get started
          </p>
        </hgroup>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-12">
        {modules.length === 0 ? (
          <p className="text-default-500 text-lg">No services available at this time.</p>
        ) : (
          <div
            className="grid gap-8 w-full max-w-5xl"
            style={{ gridTemplateColumns: `repeat(${Math.min(modules.length, 3)}, 1fr)` }}
          >
            {modules.map((module, index) => {
              const iconName = resolveModuleIconify(module.icon);
              return (
                <div
                  key={module.module_id}
                  className="animate-kiosk-slide-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <ServiceCard
                    icon={iconName}
                    title={module.label}
                    description={module.description}
                    disabled={!isOnline || (isLoading && module.url !== loadingUrl)}
                    loading={module.url === loadingUrl}
                    onPress={() =>
                      void createHandoff(module.url, {
                        icon: iconName,
                        label: module.label,
                        color: config?.branding.primary_color,
                      })
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="flex-shrink-0 flex items-center justify-center px-8 py-4 border-t border-divider bg-content1">
        <p className="text-xs text-default-500">
          Touch a card to begin · For assistance, please contact staff
          {!isOnline && (
            <span className="ml-2 text-danger font-medium">· Services temporarily offline</span>
          )}
        </p>
      </footer>

      {loadingModule && (
        <HandoffLoader
          icon={resolveModuleIconify(loadingModule.icon)}
          label={loadingModule.label}
        />
      )}
    </div>
  );
};

export default ModuleSelector;
